import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ProjectArtifactKind } from '../../models/project.models';
import { ProjectSessionService } from '../../project-session.service';

/**
 * What each wizard stage puts on screen.
 *
 * Only stages that display restored work appear here. A stage the requester
 * fills in themselves has nothing that can fall behind, and an absent entry
 * therefore means "nothing to warn about" rather than an oversight.
 */
const STAGE_ARTIFACTS: Readonly<Record<number, readonly ProjectArtifactKind[]>> = {
  1: ['project_context'],
  2: ['project_context'],
  3: ['ui_spec'],
  4: ['sbp_content', 'sbp_docx'],
};

/** How to name an artifact to someone who has never heard the word artifact. */
const ARTIFACT_LABELS: Readonly<Record<ProjectArtifactKind, string>> = {
  project_context: 'the analysis of your requirements',
  sbp_content: 'this blueprint',
  sbp_docx: 'the blueprint document',
  sbp_pptx: 'the blueprint slides',
  ui_spec: 'this UI specification',
  user_stories: 'these user stories',
  estimate: 'this estimate',
  requirements_draft: 'this requirements draft',
  pia_answers: 'these privacy answers',
  rid_answers: 'these risk answers',
};

/**
 * Tells the requester when what they are looking at cannot be trusted at face
 * value.
 *
 * Two different problems, deliberately kept apart because the answer differs.
 * Hydration failure means the saved work could not be read and the page is
 * empty through no fault of theirs - without this they would see a blank
 * wizard and reasonably conclude their project had been lost. Staleness means
 * the page is showing something real that was generated from an earlier
 * version of the brief; it is still worth reading, but approving it would
 * approve an answer to a question that has since changed.
 */
@Component({
  selector: 'app-session-notice',
  standalone: true,
  templateUrl: './session-notice.component.html',
  styleUrl: './session-notice.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionNoticeComponent {
  private readonly session = inject(ProjectSessionService);

  /** 0-based sidebar stage index of the page currently showing. */
  readonly stageIndex = input.required<number>();

  readonly hydrationFailed = this.session.hydrationFailed;

  /**
   * Named in full rather than counted. "1 item is out of date" gives the
   * requester nothing to act on; naming it tells them whether it is the part
   * they were about to sign off.
   */
  readonly staleLabels = computed<readonly string[]>(() => {
    if (this.session.hydrationFailed()) {
      return [];
    }
    const kinds = STAGE_ARTIFACTS[this.stageIndex()] ?? [];
    return kinds.filter((kind) => this.session.isStale(kind)).map((kind) => ARTIFACT_LABELS[kind]);
  });

  readonly staleMessage = computed<string | null>(() => {
    const labels = this.staleLabels();
    if (labels.length === 0) {
      return null;
    }
    const subject =
      labels.length === 1
        ? labels[0]
        : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
    return `You are looking at ${subject}, generated before the latest change to your requirements. Regenerate to bring it up to date.`;
  });
}
